import styled from '@emotion/styled';
import React, { useEffect } from 'react';
import { ProductEvent } from '~/lib/salesforce/types';
import { EventIdentifier, useEvents } from '~/shared/hooks/useEvents/useEvents';
import { TProductDetails } from '../ProductCard/ProductCard.definition';
import { SimpleProductCard } from '../SimpleProductCard';
import { useInView } from 'react-intersection-observer';
import { useFrame } from '~/shared/utils';
import { _transformProductDetailsToDataLayer } from '~/shared/hooks/useEvents/helpers';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';

type Props = {
    items: TProductDetails[];
    variant?: 'small' | 'large';
    className?: string;
    header?: string;
    listId: EventIdentifier;
    listName: EventIdentifier;
    raptorAlgorithm?: string;
    isMiniBasket?: boolean;
    isLoading?: boolean;
    isError?: boolean;
    productHandler: (listIdentifier: EventIdentifier, event: ProductEvent) => void;
    ProductLoader?: React.ComponentType;
};

export const SimpleProductList = styled((props: Props) => {
    const {
        items,
        className,
        variant,
        productHandler,
        listId,
        listName,
        header,
        raptorAlgorithm,
        isMiniBasket = false,
        isLoading,
        isError,
        ProductLoader,
    } = props;

    const { data: frame } = useFrame();
    const { isPlusMember } = useCustomer();
    const { productsImpressionsEvent } = useEvents(frame);
    const { inView, ref } = useInView({
        threshold: 0,
    });

    useEffect(() => {
        if (!inView || items.length === 0) return;

        productsImpressionsEvent(
            `${listId}`,
            `${listName} - ${header}`,
            items?.map((item, index) =>
                _transformProductDetailsToDataLayer(item, 1, index, isPlusMember),
            ),
        );
    }, [inView, items]);

    if (isError) {
        return null;
    }

    return (
        <div ref={ref} className={className}>
            {header && <h4>{header}</h4>}
            {isLoading && ProductLoader && <ProductLoader />}
            {items?.map((item, i) => (
                <SimpleProductCard
                    isMiniBasket={isMiniBasket}
                    key={item.id}
                    displaySize={variant}
                    {...item}
                    listId={listId}
                    listName={listName}
                    header={header}
                    onBuyBtn={productHandler}
                    position={i}
                    raptorAlgorithm={raptorAlgorithm}
                />
            ))}
        </div>
    );
})(({ theme }) => ({
    h4: {
        textAlign: 'center',
        marginBottom: theme.space[3],
    },
}));
